/**
 * The segmented pill the Contest Overview switches sections with
 * (design-system.md §6 "Tabs"; `.tabs` in the prototype's contest-overview.css).
 *
 * Explicitly **not** a tab strip with an underline — one rounded trough with the
 * selected tab riding on a white pill inside it.
 *
 * Hand-rolled on `ButtonBase` rather than wrapped around MUI's `Tabs` or
 * `ToggleButtonGroup`. Both bring DOM the design has to undo: `Tabs` inserts an
 * indicator span and a scroller, and `ToggleButtonGroup` collapses the adjacent
 * borders of its children, which is precisely the effect a 4px-gapped grid of
 * separate pills is not after. What they would have given us for free — the
 * tablist roles and roving focus — is the small part, and it is below.
 *
 * Roving tabindex: only the selected tab is in the page's tab order, so Tab
 * enters the strip once and leaves it, and the arrows move within. That is the
 * ARIA tabs pattern, and it is why a four-section contest does not cost a
 * keyboard user four Tab presses to step over.
 */
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { useRef, type KeyboardEvent } from 'react';
import type { Theme } from '@mui/material/styles';

/**
 * The narrowest a section tab is allowed to get before the strip reflows.
 *
 * Sized off the longest label the kit has to seat — "Fixtures" plus its count
 * badge at 36px of padding — so a tab that reaches this width still reads its
 * own name. Below it the grid wraps rather than squeezing every label at once.
 */
const TAB_MIN_WIDTH = 132;

export interface SegmentedTab {
  value: string;
  label: string;
  count?: number;
  /**
   * The section needs configuration — a draft contest's empty Reels and Prizes.
   * Turns the count badge amber to draw the eye without adding a second element.
   */
  attention?: boolean;
}

interface SegmentedTabsProps {
  /** Names the strip for assistive tech, e.g. "Contest sections". */
  label: string;
  tabs: SegmentedTab[];
  value: string;
  onChange: (value: string) => void;
  /**
   * Namespaces the tab ids as `${tabListId}-${value}`, so the panel can name
   * the tab that opened it with `aria-labelledby`.
   *
   * Optional, and only half the pair: a tab is given an id when this is set and
   * `aria-controls` when `panelId` is. Both are needed for the full ARIA tabs
   * wiring, and a screen with one strip and one panel passes both.
   */
  tabListId?: string;
  /**
   * The id of the element this strip switches between — what each tab's
   * `aria-controls` points at.
   *
   * Left off deliberately when there is no panel: the primitives gallery
   * renders a strip on its own, and `aria-controls` naming an element that is
   * not in the document is worse for a screen reader than no attribute at all.
   */
  panelId?: string;
}

export const SegmentedTabs = ({
  label,
  tabs,
  value,
  onChange,
  tabListId,
  panelId,
}: SegmentedTabsProps) => {
  const matched = tabs.findIndex((tab) => tab.value === value);
  /**
   * Falls back to the first tab when `value` matches nothing — a parent that
   * initialises it before its data loads, or a deep link carrying a section that
   * no longer exists.
   *
   * Without the fallback `findIndex`'s -1 propagates twice over: no tab gets
   * `tabIndex={0}`, so Tab skips the strip entirely with no keyboard way in; and
   * the arrow arithmetic reads `(-1 - 1 + n) % n`, landing ArrowLeft on the
   * second-to-last section instead of the last.
   *
   * Nothing is marked selected in this state — `aria-selected` compares values,
   * not indices — which is the honest rendering: the strip is reachable and
   * operable, and shows no section as current until one matches.
   */
  const selectedIndex = matched === -1 ? 0 : matched;
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;

    event.preventDefault();
    // Wraps: the strip is a ring, and stopping at the ends would make the last
    // section the awkward one to reach from the first.
    const next = (selectedIndex + step + tabs.length) % tabs.length;
    onChange(tabs[next]!.value);

    // Focus follows selection, as the ARIA tabs pattern requires. Without this
    // the roving tabindex strands focus on the tab we just left — which is now
    // `tabindex="-1"` — so a screen reader announces one tab while another is
    // selected, and the next Tab press leaves from the wrong place.
    tabRefs.current[next]?.focus();
  };

  return (
    <Box
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      sx={(theme: Theme) => ({
        display: 'grid',
        /*
         * One row of equal columns wherever there is room, wrapping to a second
         * row where there is not — rather than the handoff's flat
         * `repeat(4, 1fr)`.
         *
         * `1fr` is shorthand for `minmax(auto, 1fr)`, and that `auto` floor is
         * the column's min-content: a five-section strip then cannot shrink
         * below its five widest labels, and in any column narrower than that
         * the grid overflows and takes the page's horizontal scrollbar with it.
         * Measured before this: at a 768px viewport the Contest Overview's
         * column is 464px against a 550px min-content. The prototype never
         * meets the case — it is one fixed-width desktop page with nothing
         * around it.
         *
         * `auto-fit` collapses the tracks it does not fill, so at the design's
         * width the five sections still resolve to five equal columns, exactly
         * as the flat rule drew them. Below `TAB_MIN_WIDTH` they reflow instead.
         * `min(…, 100%)` keeps that floor from becoming a new overflow in a
         * container narrower than one tab.
         *
         * The strip this replaced reflowed too — `direction={{ xs: 'column', md:
         * 'row' }}`, a full vertical stack. It documented the same failure being
         * fixed there ("Operations overflowed 1226px at 768"). Wrapping keeps
         * the design's trough, which stacking does not. That strip
         * (`pages/contests/contest-tab-trigger.tsx`) was deleted by CAE-1556,
         * once Operations and the Overview's two nested switchers — its last
         * three callers — moved onto this one.
         */
        gridTemplateColumns: `repeat(auto-fit, minmax(min(${TAB_MIN_WIDTH}px, 100%), 1fr))`,
        gap: '4px',
        padding: '5px',
        borderRadius: `${theme.tokens.radius.pill}px`,
        backgroundColor: theme.tokens.componentColor.ringTrack,
      })}
    >
      {tabs.map((tab, index) => {
        const selected = tab.value === value;

        return (
          <ButtonBase
            key={tab.value}
            ref={(node: HTMLButtonElement | null) => {
              tabRefs.current[index] = node;
            }}
            role="tab"
            aria-selected={selected}
            id={tabListId ? `${tabListId}-${tab.value}` : undefined}
            aria-controls={panelId}
            tabIndex={index === selectedIndex ? 0 : -1}
            onClick={() => onChange(tab.value)}
            sx={(theme: Theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '11px 18px',
              // A grid item's own `min-width` is `auto` too, so the track being
              // allowed to shrink is only half of it — without this the item
              // refuses to, and overflows the column instead of the container.
              minWidth: 0,
              borderRadius: `${theme.tokens.radius.pill}px`,
              fontFamily: theme.tokens.font.sans,
              fontSize: theme.tokens.text.tab.fontSize,
              letterSpacing: '-0.005em',
              transition: 'background 0.12s',
              ...(selected
                ? {
                    backgroundColor: theme.tokens.color.surface,
                    color: theme.tokens.color.ink,
                    fontWeight: theme.tokens.tabSelectedWeight,
                    boxShadow: theme.tokens.shadow.tabSelected,
                  }
                : {
                    color: theme.tokens.color.ink3,
                    fontWeight: theme.tokens.text.tab.fontWeight,
                    '&:hover': { color: theme.tokens.color.ink2 },
                  }),
            })}
          >
            {/*
              Clipped with an ellipsis rather than wrapped: the trough is one
              row of fixed-height pills, so a second line would push the label
              out of its own pill. Only reachable once the strip is narrower
              than its labels want — at the design's width this renders exactly
              as a bare text node did.
            */}
            <Box
              component="span"
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </Box>
            {tab.count === undefined ? null : (
              <Box
                component="span"
                sx={(theme: Theme) => {
                  const { componentColor } = theme.tokens;
                  const palette = tab.attention
                    ? {
                        bg: componentColor.countBadgeAttnBg,
                        text: componentColor.countBadgeAttnText,
                      }
                    : selected
                      ? {
                          bg: componentColor.countBadgeSelectedBg,
                          text: componentColor.countBadgeSelectedText,
                        }
                      : { bg: componentColor.countBadgeBg, text: componentColor.countBadgeText };

                  return {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '20px',
                    height: '20px',
                    // The count is the part worth keeping whole — the label
                    // ellipsizes around it rather than squeezing the number.
                    flexShrink: 0,
                    padding: '0 6px',
                    borderRadius: `${theme.tokens.radius.pill}px`,
                    fontFamily: theme.tokens.font.mono,
                    fontSize: theme.tokens.text.counterBadge.fontSize,
                    fontWeight: theme.tokens.text.counterBadge.fontWeight,
                    backgroundColor: palette.bg,
                    color: palette.text,
                  };
                }}
              >
                {tab.count}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
};
